import {
  GqlModel,
  GqlModelOperator,
  GqlMutationModel,
  GqlQueryModel,
} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {QuickFormGroup} from "../helpers/quick-form-group";
import {FormControl, FormGroup} from "@angular/forms";
import {JSONModel, jsonProperty} from "../json.model";

export class AccessoryAvailableBulkOrderItemModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") quantity: string;
  @jsonProperty("string") quantityAvailable: string;
}

export class AvailableStandaloneAccessoryModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("boolean")disabled: boolean;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty([AccessoryAvailableBulkOrderItemModel]) availableBulkOrderItems: AccessoryAvailableBulkOrderItemModel[];
}

export class StandaloneAccessoryGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<StandaloneAccessoryGqlModel> =
    GqlModel.buildMapper(StandaloneAccessoryGqlModel, "standaloneAccessory");

  static formKeys: (keyof StandaloneAccessoryGqlModel)[] = [
    "id",
    "assetId",
    "siteId",
    "deviceId",
    "productNumber",
    "serialNumber",
    "building",
    "floor",
    "orderNumber",
    "orderDate",
    "bulkOrderAccessoryId",
    "useBulkOrdering",
  ];

  static query: any = gql`
    fragment bulkOrderDetails on BulkOrderLineItem {
      id
      orderNumber
      orderDate
      quantity
      quantityAvailable
    }

    query getStandaloneAccessory($accessoryId: ID, $assetId: ID, $siteId: ID) {
      standaloneAccessory: standaloneAccessory(accessoryId: $accessoryId, assetId: $assetId, siteId: $siteId) {
        id
        siteId
        assetId
        deviceId
        deviceKey
        productNumber
        serialNumber
        building
        floor
        orderNumber
        orderDate
        bulkOrderAccessoryId
        productDescription
        sentToK2
        installed
        lockedToSite
        ordered
        assetDeviceKey
        assetProductNumber
        assetProductDescription
        assetSerialNumber
        availableStandaloneAccessories {
          id
          deviceKey
          disabled
          productNumber
          productDescription
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
        }
        availableBulkOrderItems {
          ...bulkOrderDetails
        }
      }
    }
  `;

  @jsonProperty("string") id: string; // mutate - update param
  @jsonProperty("string") siteId: string; // mutate - create
  @jsonProperty("string") assetId: string; // mutate - create
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") deviceId: string; // mutate - any
  @jsonProperty("string") productNumber: string; // mutate - any
  @jsonProperty("string") serialNumber: string; // mutate - any
  @jsonProperty("string") productDescription: string;
  @jsonProperty("string") building: string; // mutate - any
  @jsonProperty("string") floor: string; // mutate - any
  @jsonProperty("string") orderNumber: string; // mutate - any
  @jsonProperty("string") orderDate: string; // mutate - any
  @jsonProperty("string") bulkOrderAccessoryId: string; // mutate - any

  useBulkOrdering: boolean = false;

  @jsonProperty("boolean") sentToK2: boolean;
  @jsonProperty("boolean") installed: boolean;
  @jsonProperty("boolean") lockedToSite: boolean;
  @jsonProperty("boolean") ordered: boolean;

  @jsonProperty("string") assetDeviceKey: string;
  @jsonProperty("string") assetProductNumber: string;
  @jsonProperty("string") assetProductDescription: string;
  @jsonProperty("string") assetSerialNumber: string;

  availableStandaloneAccessories: AvailableStandaloneAccessoryModel[];
  availableBulkOrderItems: AccessoryAvailableBulkOrderItemModel[];

  formGroup: FormGroup;

  original: boolean = true;
  keep: boolean = true;
  changed: boolean = false;

  static variables(accessoryId: string, siteId: string, assetId: string) {
    if (accessoryId) return {accessoryId};
    if (siteId && assetId) return {siteId, assetId};
    if (siteId) return {siteId};
    return {accessoryId: undefined};
  }

  constructor(data: any) {
    super(data);
    data = data || {};

    this.id = data.id;
    this.siteId = data.siteId;
    this.assetId = data.assetId;
    this.deviceKey = data.deviceKey;
    this.deviceId = data.deviceId;
    this.productNumber = data.productNumber;
    this.serialNumber = data.serialNumber;
    this.productDescription = data.productDescription;
    this.building = data.building;
    this.floor = data.floor;
    this.orderNumber = data.orderNumber;
    this.orderDate = data.orderDate;
    this.bulkOrderAccessoryId = data.bulkOrderAccessoryId;
    this.sentToK2 = data.sentToK2;
    this.installed = data.installed;
    this.lockedToSite = data.lockedToSite;
    this.ordered = data.ordered;
    this.assetDeviceKey = data.assetDeviceKey;
    this.assetProductNumber = data.assetProductNumber;
    this.assetSerialNumber = data.assetSerialNumber;
    this.availableStandaloneAccessories = data.availableStandaloneAccessories || [];
    this.availableBulkOrderItems = data.availableBulkOrderItems || [];

    for (const item of this.availableStandaloneAccessories) {
      if (!item.availableBulkOrderItems) item.availableBulkOrderItems = [];
    }

    if (this.bulkOrderAccessoryId) {
      this.useBulkOrdering = true;
    }

    this.formGroup = new QuickFormGroup(this, StandaloneAccessoryGqlModel.formKeys);

    this.formGroup.addControl("accessorySelection", new FormControl(null));
  }

  clone(): StandaloneAccessoryGqlModel {
    return new StandaloneAccessoryGqlModel(this);
  }

  export(other: StandaloneAccessoryGqlModel) {
    const keysToCopy: (keyof StandaloneAccessoryGqlModel)[] = [
      ...StandaloneAccessoryGqlModel.formKeys,
      "deviceKey",
      "productNumber",
      "productDescription",
      "availableBulkOrderItems",
    ];

    for (const key of keysToCopy) {
      if (other[key] !== this[key]) {
        other[key] = this[key];
        if (other.formGroup.controls[key]) {
          other.formGroup.controls[key].setValue(this[key]);
          other.formGroup.controls[key].markAsDirty();
        }
      }
    }
  }
}

export class StandaloneAccessoryInputResponseGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("boolean") updated: boolean;
}

export class StandaloneAccessoryInputDataGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") assetId: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") building: string;
  @jsonProperty("string") floor: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") bulkOrderAccessoryId: string;
}

export class StandaloneAccessoryInputGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<StandaloneAccessoryInputResponseGqlModel> =
    GqlModel.buildMapper(StandaloneAccessoryInputResponseGqlModel, "accessory");

  static query = gql`
    mutation updateAccessory($accessoryId: ID, $accessory: StandaloneAccessoryInput!) {
      accessory: accessory(accessoryId: $accessoryId, accessory: $accessory) {
        id
        updated
      }
    }
  `;

  @jsonProperty("string") accessoryId: string;
  @jsonProperty(StandaloneAccessoryInputDataGqlModel) accessory: StandaloneAccessoryInputDataGqlModel;
}

export class StandaloneAccessoryDeleteGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<StandaloneAccessoryInputResponseGqlModel> =
    GqlModel.buildMapper(StandaloneAccessoryInputResponseGqlModel, "accessory");

  static query = gql`
    mutation deleteAccessory($accessoryId: ID!) {
      accessory: deleteAccessory(accessoryId: $accessoryId) {
        id
        updated
      }
    }
  `;

  @jsonProperty("string") accessoryId: string;
}
